import { useEffect, useState } from "react";
import { Flame, Heart, Trophy, MessageCircle, Send } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { API, formatApiErrorDetail } from "@/lib/api";

const REACTIONS = [
  { id: "fire", label: "Puxa", Icon: Flame },
  { id: "heart", label: "Me encanta", Icon: Heart },
  { id: "trophy", label: "Victoria", Icon: Trophy },
];

const FanZone = ({ slug }) => {
  const [reactions, setReactions] = useState({ fire: 0, heart: 0, trophy: 0 });
  const [voted, setVoted] = useState({});
  const [comments, setComments] = useState([]);
  const [form, setForm] = useState({ name: "", message: "" });
  const [busy, setBusy] = useState(false);

  const loadComments = () =>
    API.get(`/articles/${slug}/comments`).then(({ data }) => setComments(data)).catch(() => {});

  useEffect(() => {
    API.get(`/articles/${slug}/reactions`).then(({ data }) => setReactions(data)).catch(() => {});
    loadComments();
    const stored = localStorage.getItem(`enolfc-react-${slug}`);
    if (stored) setVoted(JSON.parse(stored));
  }, [slug]);

  const react = async (type) => {
    if (voted[type]) return;
    try {
      const { data } = await API.post(`/articles/${slug}/react`, { type });
      setReactions(data);
      const nv = { ...voted, [type]: true };
      setVoted(nv);
      localStorage.setItem(`enolfc-react-${slug}`, JSON.stringify(nv));
    } catch {
      toast.error("No se pudo registrar la reacción");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await API.post(`/articles/${slug}/comments`, form);
      toast.success("¡Comentario publicado! Puxa Sporting");
      setForm({ name: "", message: "" });
      loadComments();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const field =
    "w-full rounded-xl border border-edge bg-ink2 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-crimson focus:outline-none transition-colors";

  return (
    <section className="glass rounded-3xl p-6 md:p-8" data-testid="fanzone">
      <p className="eyebrow mb-2">Zona de afición</p>
      <h2 className="font-display text-2xl font-extrabold uppercase text-white sm:text-3xl">
        ¿Cómo lo viste tú?
      </h2>

      <div className="mt-5 flex flex-wrap gap-3">
        {REACTIONS.map(({ id, label, Icon }) => (
          <motion.button
            key={id}
            data-testid={`react-${id}`}
            onClick={() => react(id)}
            whileTap={{ scale: 0.92 }}
            disabled={!!voted[id]}
            className={`flex items-center gap-2 rounded-full border px-5 py-2.5 font-display text-base font-bold uppercase tracking-wider transition-colors ${
              voted[id]
                ? "border-crimson bg-crimson/20 text-glow"
                : "border-edge text-slate-300 hover:border-crimson hover:text-white"
            }`}
          >
            <Icon className="h-4 w-4" /> {label}
            <span className="font-mono-score text-sm text-slate-400">{reactions[id] ?? 0}</span>
          </motion.button>
        ))}
      </div>

      <div className="mt-8 border-t border-edge pt-6">
        <h3 className="flex items-center gap-2 font-display text-lg font-bold uppercase text-white">
          <MessageCircle className="h-4 w-4 text-crimson" /> Comentarios ({comments.length})
        </h3>
        <form onSubmit={submit} className="mt-4 space-y-3" data-testid="comment-form">
          <input
            required
            maxLength={40}
            data-testid="comment-input-name"
            placeholder="Tu nombre o apodo"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={field}
          />
          <textarea
            required
            rows={3}
            maxLength={500}
            data-testid="comment-input-message"
            placeholder="Escribe tu comentario, rojiblanco..."
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            className={field}
          />
          <button
            type="submit"
            disabled={busy}
            data-testid="comment-submit"
            className="flex items-center gap-2 rounded-full bg-crimson px-6 py-3 font-display text-base font-bold uppercase tracking-wider text-white transition-transform hover:scale-105 disabled:opacity-50"
          >
            <Send className="h-4 w-4" /> {busy ? "Publicando..." : "Publicar comentario"}
          </button>
        </form>

        <div className="mt-6 space-y-4" data-testid="comments-list">
          {comments.length === 0 && (
            <p className="text-sm text-slate-500">Sé el primero en comentar esta crónica.</p>
          )}
          {comments.map((c) => (
            <div key={c.id} className="rounded-2xl border border-edge bg-ink p-4" data-testid={`comment-${c.id}`}>
              <p className="font-display text-sm font-bold uppercase text-glow">
                {c.name}
                <span className="ml-2 font-mono-score text-[10px] font-normal normal-case tracking-widest text-slate-500">
                  {c.created_at?.slice(0, 10)}
                </span>
              </p>
              <p className="mt-1 text-sm leading-relaxed text-slate-300">{c.message}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FanZone;
